import { useEffect, useCallback, useContext, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import { Box } from "@mui/system"; // CHECK: Should this be imported from @mui/material?
import { makeStyles } from "@mui/styles";
import { getCorpusMetaData } from "../../services/CorpusMetaData";
import FilterSidebar from "./FilterSidebar";
import TableComponent from "./TableComponent";
import CorpusHeader from "./CorpusHeader";
import NavigationAndStats from "./NavigationAndStats";
import SearchFilters from "./SearchFilter";
import PaginationComponent from "../Common/PaginationComponent";
import { Context } from "../../App";
import { cleanSearchPagination } from "../../utility/Helper";
import CorpusMetadataTour, { TOUR_KEY } from "../GuidedTour/CorpusMetadataTour";
import { DEMO_BOOKS } from "../GuidedTour/PairwiseVisualizationTour";
import { DEMO_BOOKS as CORPUSWIDE_DEMO_BOOKS } from "../GuidedTour/CorpuswideVisualizationTour";
import { MAIN_TOUR_REQUEST } from "../GuidedTour/TourMenu";

// make custom style for material ui component
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  tableHeaderCell: {
    fontSize: 14,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    textAlign: "right",
  },
  tableCell: {
    textAlign: "right",
    verticalAlign: "top",
  },
  gridContainer: {
    [theme.breakpoints.down("sm")]: {
      padding: "20px",
    },
    [theme.breakpoints.between("sm", "lg")]: {
      padding: "0px 3%",
    },
    [theme.breakpoints.up("lg")]: {
      padding: "0px 100px",
    },
  },
}));

const MetadataTable = ({ isHome }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tourRunning, setTourRunning] = useState(false);
  const { version } = useParams();
  //const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();
  const {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    rows,
    setRows,
    releaseCode,
    setReleaseCode,
    setStatus,
    query,
    setQuery,
    searchField,
    normalizedSearch,
    setNormalizedSearch,
    sortingOrder,
    setOrderingOrder,
    showPrimary,
    setShowPrimary,
    showSecondary,
    setShowSecondary,
    annotationFilter,
    setAnnotationFilter,
    totalRecords,
    setTotal,
    checkedNotification,
    setCheckedNotification,
    advanceSearch,
    setAdvanceSearch,
    activeTextTypes,
    setActiveTextTypes,
    activeLanguages,
    setActiveLanguages,
    setFilterPanel,
    toggleSidePanel,
    setIsOpenDrawer
  } = useContext(Context);

  // update orders
  const updateOrderingOrder = useCallback(() => {
    if (searchParams.has("ordering")) {
      setOrderingOrder(searchParams.get("ordering"));
    } else {
      setOrderingOrder("");
    }
  }, [searchParams, setOrderingOrder]);

  useEffect(() => {
    updateOrderingOrder();
  }, [updateOrderingOrder]);

  const updatePage = useCallback(() => {
    if (searchParams.has("page")) {
      setPage(parseInt(searchParams.get("page")));
    } else {
      setPage(1);
    }
  }, [searchParams, setPage]);

  useEffect(() => {
    updatePage();
  }, [updatePage]);

  const updatePageRow = useCallback(() => {
    if (searchParams.has("rowsPerPage")) {
      setRowsPerPage(parseInt(searchParams.get("rowsPerPage")));
    } else {
      setRowsPerPage(10);
    }
  }, [searchParams, setRowsPerPage]);

  useEffect(() => {
    updatePageRow();
  }, [updatePageRow]);

  // function for reset filters
  const handleResetFilters = () => {
    //setQuery("");
    setOrderingOrder("");
    setOrderingOrder("");
    setShowPrimary(true);
    setShowSecondary(false);
    setPage(1);
    setNormalizedSearch(true);
    setRowsPerPage(10);
    setAnnotationFilter({
      completed: false,
      inProgress: false,
      mARkdown: false,
      notYetAnnotated: false,
    });
    setActiveTextTypes([]);
    setActiveLanguages([]);
    setSearchParams({ version: 'pri', text_type: 'all', language: 'all' });
    setAdvanceSearch({
      // max_char_count: "",
      // min_char_count: "",
      max_tok_count: "",
      min_tok_count: "",
      editor: "",
      edition_place: "",
      edition_date: "",
      died_before_AH: "",
      died_after_AH: "",
    });
  };

  useEffect(() => {
    if (searchParams.get("search")) {
      setQuery(searchParams.get("search"));
    }
  }, [searchParams, setQuery]);

  // auto-start tour on first visit, or when requested from the visualisation page
  useEffect(() => {
    let requested = false;
    try {
      requested = sessionStorage.getItem(MAIN_TOUR_REQUEST) === "1";
    } catch (e) { /* sessionStorage unavailable */ }
    if (requested) {
      // give the table time to render, so that the tour finds its targets
      const timer = setTimeout(() => {
        // (removed here rather than above, so that a re-run of this effect finds it)
        try { sessionStorage.removeItem(MAIN_TOUR_REQUEST); } catch (e) { /* ignore */ }
        setTourRunning(true);
      }, 1200);
      return () => clearTimeout(timer);
    }
    if (!localStorage.getItem(TOUR_KEY)) {
      setTourRunning(true);
    }
  }, []);

  const updateVersion = useCallback(() => {
    const v = searchParams.get("version");
    if (v === "all") { setShowPrimary(true); setShowSecondary(true); }
    else if (v === "sec") { setShowPrimary(false); setShowSecondary(true); }
    else if (v === "none") { setShowPrimary(false); setShowSecondary(false); }
    else { setShowPrimary(true); setShowSecondary(false); } // "pri" or missing
  }, [searchParams, setShowPrimary, setShowSecondary]);

  useEffect(() => { updateVersion(); }, [updateVersion]);

  const updateTextTypes = useCallback(() => {
    const tt = searchParams.get("text_type");
    if (!tt || tt === "all") { setActiveTextTypes([]); }
    else { setActiveTextTypes(tt.split(",")); }
  }, [searchParams, setActiveTextTypes]);

  useEffect(() => { updateTextTypes(); }, [updateTextTypes]);

  const updateLanguages = useCallback(() => {
    const lang = searchParams.get("language");
    if (!lang || lang === "all") { setActiveLanguages([]); }
    else { setActiveLanguages(lang.split(",")); }
  }, [searchParams, setActiveLanguages]);

  useEffect(() => { updateLanguages(); }, [updateLanguages]);

  useEffect(() => {
    if (version) {
      setReleaseCode(version);
      localStorage.setItem("release_code", JSON.stringify(version));
    } else {
      const storedReleaseCode = localStorage.getItem("release_code");
      if (storedReleaseCode) {
        setReleaseCode(JSON.parse(storedReleaseCode));
      } else {
        setReleaseCode(releaseCode);
        localStorage.setItem("release_code", JSON.stringify(releaseCode));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isChecked = (value) => {
      if (searchParams.get("annotation_status")) {
        const annotationStatusArr = searchParams
          .get("annotation_status")
          .split(",");
        const isExist = annotationStatusArr.filter((item) => {
          return item === value;
        });
        if (isExist.length !== 1) {
          return false;
        } else {
          return true;
        }
      }
    };

    setAnnotationFilter({
      notYetAnnotated: searchParams.get("annotation_status")
        ? isChecked("notYetAnnotated")
        : false,
      inProgress: searchParams.get("annotation_status")
        ? isChecked("inProgress")
        : false,
      completed: searchParams.get("annotation_status")
        ? isChecked("completed")
        : false,
      mARkdown: searchParams.get("annotation_status")
        ? isChecked("mARkdown")
        : false,
    });
  }, [searchParams, setAnnotationFilter]);

  useEffect(() => {
    setStatus("loading");
    // only send the subcorpora param when some are excluded; when all are
    // active (activeSubcorpora matches the full release list) send nothing
    // only send language param when a subset is selected; [] means all active
    const languageQuery = activeLanguages.length > 0 ? activeLanguages.join(",") : "";
    const getData = async () => {
      const data = await getCorpusMetaData(
        page,
        rowsPerPage,
        query,
        searchField,
        normalizedSearch,
        annotationFilter,
        sortingOrder,
        showPrimary,
        showSecondary,
        releaseCode,
        advanceSearch,
        activeTextTypes,
        languageQuery
      );
      setRows(data.results);
      setStatus("loaded");
      setTotal(data.count);
    };

    getData();
  }, [
    page,
    rowsPerPage,
    query,
    searchField,
    normalizedSearch,
    annotationFilter,
    sortingOrder,
    showPrimary,
    showSecondary,
    releaseCode,
    checkedNotification,
    setCheckedNotification,
    setRows,
    setStatus,
    setTotal,
    advanceSearch,
    activeTextTypes,
    activeLanguages,
  ]);
  useEffect(() => {
    /*// always reset filters when switching releases so the user starts clean;
    // the toggles are rendered in FilterSidebar based on the release's fields
    setShowPrimary(true);
    setShowSecondary(false);
    setActiveTextTypes([]);
    setActiveLanguages([]);
    setPage(1);
    // combine path + default search params in a single navigate to avoid conflicts;
    // preserve the current search query if present
    const currentQuery = searchParams.get("search");
    const queryPart = currentQuery ? `&search=${encodeURIComponent(currentQuery)}` : "";
    const search = `?version=pri&text_type=all&language=all${queryPart}`;
    */
    // reset only the page filter when changing release version:
    const params = cleanSearchPagination(searchParams); // strips page
    const search = Object.entries(params).map(([k, v]) => `${encodeURIComponent(k)}=${v}`).join("&");

    if (!isHome) {
      navigate(`/metadata/${releaseCode}/?${search}`);
    } else {
      navigate(`/${releaseCode}/?${search}`);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseCode]);

  return (
    <>
      <CorpusMetadataTour 
        run={tourRunning} 
        onExit={() => setTourRunning(false)} 
        setFilterPanel={setFilterPanel} 
        toggleSidePanel={toggleSidePanel}
        setIsOpenDrawer={setIsOpenDrawer}
      />
      <Grid container id="corpus-metadata" className={classes.gridContainer}>
        <CorpusHeader
          onStartTour={(key) => {
            if (key === "main") setTourRunning(true);
            else if (key === "corpuswide") {
              try { sessionStorage.setItem("kitab-start-corpuswide-tour", String(Date.now())); } catch (e) { /* ignore */ }
              navigate(`/visualise/${releaseCode}/?books=${CORPUSWIDE_DEMO_BOOKS}&tour=corpuswide`);
            } else if (key === "pairwise") {
              try { sessionStorage.setItem("kitab-start-pairwise-tour", String(Date.now())); } catch (e) { /* ignore */ }
              navigate(`/visualise/${releaseCode}/?books=${DEMO_BOOKS}&tour=pairwise`);
            }
          }}
        />

        <Box
          width={"100%"}
          display={"flex"}
          alignItems={"flex-start"}
        >
          <FilterSidebar handleResetFilters={handleResetFilters} />

          <Box
            sx={{
              transition: "width .3s",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                overflow: "hidden",
                minHeight: "400px",
              }}
            >
              <Grid>
                <SearchFilters
                  handleResetFilters={handleResetFilters}
                  getQuery={(q) => {
                    setQuery(q);
                  }}
                />

                <NavigationAndStats />

                {rows ? (
                  <TableComponent classes={classes} />
                ) : (
                  <Box
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    minHeight={"300px"}
                  >
                    <Typography variant="h4">No Data Found</Typography>
                  </Box>
                )}

                {totalRecords ? <PaginationComponent /> : ""}
              </Grid>
            </Box>
          </Box>
        </Box>
      </Grid>
    </>
  );
};
export default MetadataTable;
